<script setup lang="ts">
import IconCheck from '@assets/icons/check.svg?component';
import IconClock from '@assets/icons/clock.svg?component';
import BaseButton from '@components/quiz_v2/BaseButton/BaseButton.vue';
import BaseSpinner from '@components/quiz_v2/BaseSpinner/BaseSpinner.vue';
import IconArrowRight from '@components/quiz_v2/QuizCheckout/assets/icons/arrow-right.svg';
import {
  STEP_CONTENT_TYPE,
  type Step,
  type StepQualiphyContent,
} from '@models/quiz';
import { useQuizStore, useUserStore } from '@stores/index';
import { computed, onMounted, ref, watch } from 'vue';
import { useQualiphy } from './useQualiphy';

const props = defineProps<{
  step: Step
}>();

const dashboardUrl = '/patient';
const ordersUrl = '/patient/orders';

const quizStore = useQuizStore();
const userStore = useUserStore();
const qualiphyRequested = ref(false);

const content = computed(() => {
  return props.step.content.find((item) => {
    return item.type === STEP_CONTENT_TYPE.QUALIPHY_CONTENT;
  })?.content as StepQualiphyContent | undefined;
});

const items = computed(() => content.value?.items ?? []);

const isSyncReview = computed(() => {
  return userStore?.profileInfo?.isTwoWayState ?? false;
});

const visibleItems = computed(() => {
  return items.value.filter((item) => {
    return !(item.type === 'syncReview' && !isSyncReview.value)
      && !(item.type === 'asyncReview' && isSyncReview.value);
  });
});

const hasSyncReviewItem = computed(() => {
  return items.value.some((item) => item.type === 'syncReview');
});

const {
  getQualiphy,
  getQualiphyLoading,
  isQualiphyAvailable,
  meetingUrl,
} = useQualiphy();

watch(
  () => ({
    syncReview: isSyncReview.value,
    hasSyncReview: hasSyncReviewItem.value,
    patientId: userStore.info?.id,
    submissionId: quizStore.submissionId,
  }),
  async ({
    syncReview,
    hasSyncReview,
    patientId,
    submissionId,
  }) => {
    if (!syncReview || !hasSyncReview || !patientId || !submissionId || qualiphyRequested.value) {
      return;
    }

    qualiphyRequested.value = true;

    try {
      await getQualiphy();
    } catch {
      qualiphyRequested.value = false;
    }
  },
  { immediate: true },
);

onMounted(async () => {
  await userStore.refreshUserProfile();
});
</script>

<template>
  <div
    class="f-row"
    data-testpl="step-qualiphy"
  >
    <div class="f-col-full sm:f-col-8">
      <ul
        class="flex flex-col gap-16"
        data-testpl="step-qualiphy-list"
      >
        <li
          v-for="(item, index) in visibleItems"
          :key="`${item.type}-${index}`"
          class="relative flex gap-20 rounded-[16px] border border-warm-grey-30 bg-white/80 p-20 transition-colors hover:bg-white sm:items-center sm:gap-24 sm:p-24"
          data-testpl="step-qualiphy-item"
        >
          <figure
            class="sm:size-96 size-80 shrink-0 overflow-hidden rounded-[10px] bg-warm-grey-30"
            data-testpl="step-qualiphy-item-img"
          >
            <img
              v-if="item.image"
              :src="item.image"
              class="size-full object-cover"
              alt=""
            >
          </figure>

          <div class="pr-38 min-w-0 grow sm:pr-0">
            <p
              v-if="item.label"
              class="text-12 font-bold uppercase text-primary-500"
              data-testpl="step-qualiphy-item-type"
            >
              {{ item.label }}
            </p>

            <p
              v-if="item.titleField"
              class="mt-8 text-20 font-bold leading-125 text-deep-blue sm:text-22"
              data-testpl="step-qualiphy-item-title"
            >
              {{ item.titleField }}
            </p>

            <div
              v-if="item.description"
              class="prose mt-8 text-14 text-deep-blue-50"
              data-testpl="step-qualiphy-item-subtitle"
              v-html="item.description"
            />

            <div
              v-if="item.type === 'order'"
              class="mt-20"
            >
              <BaseButton
                :href="ordersUrl"
                theme="outlined"
                size="sm"
                data-testpl="step-qualiphy-order-btn"
              >
                See order details
              </BaseButton>
            </div>

            <template v-else-if="item.type === 'syncReview'">
              <div
                v-if="getQualiphyLoading"
                class="mt-20 flex items-center gap-12 text-deep-blue-50"
              >
                <BaseSpinner class="size-20 text-primary-500" />
                <p class="text-14">
                  Your provider review is being prepared
                </p>
              </div>

              <div
                v-else-if="meetingUrl"
                class="mt-20"
              >
                <BaseButton
                  :href="meetingUrl"
                  color="black"
                  size="sm"
                  data-testpl="step-qualiphy-start-session-btn"
                  animated
                >
                  {{ isQualiphyAvailable ? 'Start session now' : 'Start your visit' }}
                  <template #icon-right>
                    <IconArrowRight />
                  </template>
                </BaseButton>

                <template v-if="isQualiphyAvailable">
                  <p class="mt-20 text-14 text-deep-blue-50">
                    Can't connect with your provider now?
                  </p>

                  <BaseButton
                    class="mt-8"
                    :href="meetingUrl"
                    theme="outlined"
                    size="sm"
                    data-testpl="step-qualiphy-schedule-session-btn"
                  >
                    Start your visit
                  </BaseButton>
                </template>

                <p
                  v-if="!isQualiphyAvailable && item.availability"
                  class="mt-16 text-12 text-deep-blue-50"
                >
                  {{ item.availability }}
                </p>
              </div>
            </template>

            <div
              v-else-if="item.type === 'asyncReview'"
              class="mt-20"
            >
              <BaseButton
                :href="dashboardUrl"
                color="black"
                size="sm"
                data-testpl="step-qualiphy-dashboard-btn"
                animated
              >
                Explore health dashboard
                <template #icon-right>
                  <IconArrowRight />
                </template>
              </BaseButton>
            </div>
          </div>

          <div class="absolute right-20 top-20 size-32 shrink-0 sm:static">
            <div
              v-if="item.type === 'order'"
              class="flex size-full items-center justify-center rounded-full border border-deep-blue text-deep-blue"
            >
              <IconCheck class="size-18" />
            </div>

            <div
              v-else-if="item.type === 'shipment'"
              class="flex size-full items-center justify-center rounded-full bg-warm-grey-30 text-warm-grey"
            >
              <IconClock class="size-18" />
            </div>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>
